import * as React from 'react';
import { Box, Grid } from '@mui/material';
import { Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { getZonesArrayFromString } from 'components/location/helpers';
import {
    REQUIRED_ERROR_MESSAGE,
    TABLE_NUMBER_CHARACTERS_ERROR,
    TABLE_ZONE_ALPHANUMERIC_CHARACTERS_ERROR
} from 'config/messages';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { MuiModal, MuiModalProps } from 'ui/MuiModal';
import { MuiForm } from 'lib/Form';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';

export interface ITableZoneForm {
    zoneId: string;
    tables: string;
    // Zonal/Comtrex fields
    salesAreaId?: number;
    salesAreaIdItems?: number;
    salesAreaIdDiscounts?: number;
    salesAreaIdPayments?: number;
}

const validationSchema = Yup.object().shape({
    zoneId: Yup.string()
        .required(REQUIRED_ERROR_MESSAGE)
        .matches(/^[a-zA-Z0-9_]*$/, TABLE_ZONE_ALPHANUMERIC_CHARACTERS_ERROR),
    tables: Yup.string()
        .required(REQUIRED_ERROR_MESSAGE)
        .matches(/[0-9]+(?:-[0-9]+)?(,[0-9]+(?:-[0-9]+)?)*/, TABLE_NUMBER_CHARACTERS_ERROR)
        .test('isValidZone', TABLE_NUMBER_CHARACTERS_ERROR, tableZones => {
            const zonesArray = getZonesArrayFromString(tableZones ?? '');

            return !zonesArray.some(zone => zone.includes(NaN) || zone.includes(0));
        }),
    salesAreaId: Yup.string().matches(/^[1-9]\d*$/, 'Please enter a positive number'),
    salesAreaIdItems: Yup.string().matches(/^[1-9]\d*$/, 'Please enter a positive number'),
    salesAreaIdDiscounts: Yup.string().matches(/^[1-9]\d*$/, 'Please enter a positive number'),
    salesAreaIdPayments: Yup.string().matches(/^[1-9]\d*$/, 'Please enter a positive number')
});

const TableZoneForm: React.FC<{ isZonalOrComtrex: boolean }> = ({ isZonalOrComtrex }) => {
    const { submitForm } = useFormikContext<ITableZoneForm>();

    return (
        <Grid container>
            <Box width="100%" mt={2}>
                <TextFormField
                    description="Enter the table numbers you'd like this zone to be applied to. Each table should be
                    separated with a comma and use a dash for a range of tables e.g. 1-10,60,70-75."
                    label={<MuiFormLabel required>Table Number</MuiFormLabel>}
                    name="tables"
                />
            </Box>

            <Box width="100%" mt={2}>
                <TextFormField label={<MuiFormLabel required>Zone</MuiFormLabel>} name="zoneId" />
            </Box>

            {isZonalOrComtrex && (
                <>
                    <Box width="100%" mt={2}>
                        <TextFormField label="Sales Area ID" name="salesAreaId" inputMode="numeric" />
                    </Box>
                    <Box width="100%" mt={2}>
                        <TextFormField
                            label="Sales Area ID Items"
                            name="salesAreaIdItems"
                            inputMode="numeric"
                        />
                    </Box>
                    <Box width="100%" mt={2}>
                        <TextFormField
                            label="Sales Area ID Discounts"
                            name="salesAreaIdDiscounts"
                            inputMode="numeric"
                        />
                    </Box>
                    <Box width="100%" mt={2}>
                        <TextFormField
                            label="Sales Area ID Payments"
                            name="salesAreaIdPayments"
                            inputMode="numeric"
                        />
                    </Box>
                </>
            )}

            <Box width="100%" display="flex" mt={2} justifyContent="flex-end" alignItems="center">
                <LoadingButton color="primary" variant="contained" onClick={submitForm}>
                    Save
                </LoadingButton>
            </Box>
        </Grid>
    );
};

interface ICreateTableZoneModalProps {
    onAddition: (zoneDetails: ITableZoneForm) => void;
    onClose: () => void;
    title: string;
    open: boolean;
    isZonalOrComtrex: boolean;
}

export const CreateTableZoneModal: React.FC<ICreateTableZoneModalProps> = ({
    onAddition,
    isZonalOrComtrex,
    ...modalProps
}) => {
    const initialValues: ITableZoneForm = {
        zoneId: '',
        tables: '',
        salesAreaId: undefined,
        salesAreaIdItems: undefined,
        salesAreaIdDiscounts: undefined,
        salesAreaIdPayments: undefined
    };

    const handleSubmit = React.useCallback(
        (values: ITableZoneForm) => {
            onAddition(values);
        },
        [onAddition]
    );

    return (
        <MuiModal {...modalProps}>
            <MuiForm
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
            >
                <Form>
                    <TableZoneForm isZonalOrComtrex={isZonalOrComtrex} />
                </Form>
            </MuiForm>
        </MuiModal>
    );
};

interface IEditTableZoneModalProps extends Omit<MuiModalProps, 'children'> {
    initialValues: ITableZoneForm;
    onEdit: (values: ITableZoneForm) => void;
    open: boolean;
    onClose: () => void;
    isZonalOrComtrex: boolean;
}

export const EditTableZoneModal: React.FC<IEditTableZoneModalProps> = ({
    initialValues = {},
    onEdit,
    isZonalOrComtrex,
    ...modalProps
}) => {
    const handleSubmit = React.useCallback(
        (values: ITableZoneForm) => {
            onEdit(values);
        },
        [onEdit]
    );

    return (
        <MuiModal {...modalProps} title={`Edit ${initialValues.zoneId}`}>
            <MuiForm
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
            >
                <Form>
                    <TableZoneForm isZonalOrComtrex={isZonalOrComtrex} />
                </Form>
            </MuiForm>
        </MuiModal>
    );
};
